// src/oura/components/visibility/NamedOverTime.tsx
//
// Named over time: one point per scan, for the engine the page is filtered to.
//
// A point measured another way (an older question set, or ChatGPT asked
// through the API rather than the app) stands apart: grey, unjoined, on the
// far side of a dashed rule. Only like-for-like scans join with a solid line,
// because a re-author alone has moved the same business from 30% to 52%
// (trendBreak.ts), and a solid line across that is a claim about the business
// that is not true. Neighbouring scans on different question sets join with
// a dotted line instead, named in a key under the chart (Jack, 22 Oct 2026).
//
// With two or more question sets on record, the line is drawn by set
// instead (VersionedChart), for staff and client alike: a tab per set
// (v1, v2, …), and clicking a tab or a point picks that set. The picked set
// is drawn blue on a tinted band, joined, with its own figures under the
// chart and the way into Question history; every other set stays grey, its
// own scans joined since they asked the same questions. Two scans on one day
// carry their time under the date, so neither label repeats the other. The
// Versions menu narrows the chart to the sets ticked in it: a set left out
// drops off the chart, its scale and its spacing.

import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { ChevronRightIcon, ListFilterIcon, ShieldCheckIcon, TrendingUpIcon } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@component/dropdown-menu";
import { EngineLogo, FoldCard, OfValue, PillButton, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { namedRule } from "../../visibility/model";
import type { EngineFilter, VisModel, VisOpsRow, VisTrendBreakCause } from "../../visibility/model";
import { crowdedDays, dayMonth, hourMinute, onCrowdedDay } from "../../visibility/format";
import { versionName } from "../../visibility/questionSets";

interface ChartPoint {
    date: string | null;
    value: number;
    /** What the value is out of on that scan, when the history says. */
    of: number | null;
    /** Measured before the break. */
    old: boolean;
    /** The question set it was asked on, when the history says. */
    set: number | null;
}

/** Two neighbouring scans asked on different question sets: joined by a dotted line, not a solid one. */
const DOTTED = { stroke: "#0B49FE", strokeOpacity: 0.35, strokeWidth: 1.5, strokeDasharray: "4 4" } as const;

/**
 * How two neighbouring points join: solid when like for like, dotted across a
 * change of questions, not at all across any other break (ChatGPT asked
 * another way, measured differently), which stays the unjoined rule it was.
 */
type JoinKind = "solid" | "dotted" | "none";

function joinKind(a: ChartPoint, b: ChartPoint, breakCause: string | null): JoinKind {
    if (a.old !== b.old) return breakCause === "questions" ? "dotted" : "none";
    // The same side of the break: different sets, when the history says, are not like for like.
    if (a.set !== null && b.set !== null && a.set !== b.set) return "dotted";
    return "solid";
}

/** What the two kinds of line mean, under the chart. */
function LineKey() {
    const swatch = (dotted: boolean) => (
        <svg aria-hidden width="20" height="6" className="shrink-0">
            <line x1="1" y1="3" x2="19" y2="3" stroke="#0B49FE" strokeWidth={dotted ? 1.5 : 2} strokeOpacity={dotted ? 0.35 : 1} strokeDasharray={dotted ? "4 4" : undefined} />
        </svg>
    );
    return (
        <div className={cn(TYPE.tm, "mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-500")}>
            <span className="inline-flex items-center gap-1.5">{swatch(false)}Same questions</span>
            <span className="inline-flex items-center gap-1.5">{swatch(true)}Questions changed</span>
        </div>
    );
}

/** The note under a chart with a dotted line. */
const DOTTED_NOTE = "A dotted line joins scans on different questions, so the change between them is not like for like.";

/**
 * Drawn at the card's own pixel width and a fixed height, so the axis text
 * is 12px at every width. A viewBox scaled to fit shrank it to about 7px on
 * a narrow card.
 */
const H = 216;
const X0 = 28;
const TOP = 26;
const BOT = H - 28;
const STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
/** The room a date label ("13 Oct") needs on the axis, with air either side. */
const LABEL_ROOM = 52;
/** Rough px per character of 12px system sans, to decide which side of the rule a label sits on. */
const CHAR_PX = 6.6;

/** Three gridlines that clear the tallest point with room for its label. */
function gridStep(max: number): number {
    const target = Math.max(3, max * 1.15) / 3;
    return STEPS.find((s) => s >= target) ?? Math.ceil(target);
}

/** The element's content width, kept current as the layout changes. */
function useWidth<T extends HTMLElement>() {
    const ref = useRef<T>(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        setWidth(el.clientWidth);
        const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return [ref, width] as const;
}

interface ChartProps {
    points: ChartPoint[];
    unit: string;
    /** The words at the dashed rule, and after an old point in the spoken label. */
    breakLabel: string;
    oldLabel: string;
    /** The merchant's scan number for a point, by its date ("V2"): the one the scan picker and reports use. */
    scanOf?: (date: string | null) => number | null;
    /** What the break is (`trend.breakCause`): only a change of questions is joined across, dotted. */
    breakCause: string | null;
}

/** The chart at the width its card gives it. */
function TrendChart(props: ChartProps) {
    const [ref, width] = useWidth<HTMLDivElement>();
    return (
        <div ref={ref} className="w-full" style={{ height: H }}>
            {width > 0 && <TrendSvg {...props} W={width} />}
        </div>
    );
}

function TrendSvg({ points, unit, breakLabel, oldLabel, scanOf, breakCause, W }: ChartProps & { W: number }) {
    const X1 = W - 28;
    const step = gridStep(Math.max(...points.map((p) => p.value)));
    const top = step * 3;
    const p0 = X0 + 30;
    const p1 = X1 - 10;
    const x = (i: number) => (points.length === 1 ? (p0 + p1) / 2 : p0 + (i * (p1 - p0)) / (points.length - 1));
    const y = (v: number) => BOT - (v / top) * (BOT - TOP);
    const xy = points.map((p, i) => ({ ...p, x: x(i), y: y(p.value) }));
    const current = xy.filter((p) => !p.old);
    const old = xy.filter((p) => p.old);
    // Like-for-like neighbours join solid, in runs; any other neighbours join dotted.
    const solidRuns: (typeof xy)[] = [];
    xy.forEach((p, i) => {
        const run = solidRuns[solidRuns.length - 1];
        if (i > 0 && run && joinKind(xy[i - 1], p, breakCause) === "solid") run.push(p);
        else solidRuns.push([p]);
    });
    const dotted = xy.slice(1).flatMap((p, i) => (joinKind(xy[i], p, breakCause) === "dotted" ? [[xy[i], p] as const] : []));
    const pts = (run: typeof xy) => run.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const brk = old.length && current.length ? (old[old.length - 1].x + current[0].x) / 2 : null;
    // The words at the rule sit to its right, unless they would run past the
    // svg (a break near the newest scan): then they end at the rule instead of
    // being clipped ("Questions char").
    const markLeft = brk !== null && brk + 6 + breakLabel.length * CHAR_PX > W - 4;
    const label = points
        .map((p) => `${dayMonth(p.date) || "undated"}: ${p.value}${p.old ? `, ${oldLabel}` : ""}`)
        .join("; ");
    // ONLY AS MANY AXIS LABELS AS FIT (16 Oct 2026): sixteen "V12 · 13 Oct"
    // labels ran into one another. Every Nth point is labelled, the newest
    // always, and the rest get a tick; every point names itself on hover.
    const lastI = points.length - 1;
    const span = points.length > 1 ? (p1 - p0) / lastI : Infinity;
    const every = Math.max(1, Math.ceil((scanOf ? 84 : LABEL_ROOM) / span));
    const labelled = (i: number) => i === lastI || (i % every === 0 && lastI - i >= every);
    const axisText = (p: ChartPoint) => {
        const n = scanOf?.(p.date) ?? null;
        return n ? `V${n} · ${dayMonth(p.date)}` : dayMonth(p.date);
    };

    return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block" role="img" aria-label={`${unit} per scan. ${label}`}>
            {[1, 2, 3].map((k) => (
                <g key={k}>
                    <line x1={X0} y1={y(step * k)} x2={X1} y2={y(step * k)} stroke="#F3F4F6" />
                    <text x={X0 - 8} y={y(step * k) + 4} textAnchor="end" fontSize="12" fill="#9CA3AF">{step * k}</text>
                </g>
            ))}
            <line x1={X0} y1={BOT} x2={X1} y2={BOT} stroke="#E5E7EB" />
            {dotted.map(([a, b]) => (
                <line key={`dot-${a.x}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} {...DOTTED} />
            ))}
            {solidRuns.filter((run) => run.length > 1).map((run) => (
                <g key={`run-${run[0].x}`}>
                    {!run[0].old && (
                        <polygon points={`${run[0].x},${BOT} ${pts(run)} ${run[run.length - 1].x},${BOT}`} fill="#0B49FE" fillOpacity="0.06" />
                    )}
                    <polyline points={pts(run)} fill="none" stroke={run[0].old ? "#D1D5DB" : "#0B49FE"} strokeWidth="2" strokeLinejoin="round" />
                </g>
            ))}
            {brk !== null && (
                <>
                    <line x1={brk} y1={TOP - 14} x2={brk} y2={BOT} stroke="#D1D5DB" strokeDasharray="3 4" />
                    <text x={markLeft ? brk - 6 : brk + 6} y={TOP - 4} textAnchor={markLeft ? "end" : "start"} fontSize="12" fill="#9CA3AF">
                        {breakLabel}
                    </text>
                </>
            )}
            {xy.map((p, i) => {
                const last = !p.old && i === xy.length - 1;
                return (
                    <g key={`${p.date ?? "p"}-${i}`}>
                        <title>{`${axisText(p) || "Undated"}: ${p.value}`}</title>
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r={last ? 4.5 : 3.5}
                            fill={last ? "#0B49FE" : "#fff"}
                            stroke={p.old ? "#D1D5DB" : "#0B49FE"}
                            strokeWidth="2"
                        />
                        <text x={p.x} y={p.y - 11} textAnchor="middle" fontSize="13" fontWeight="600" fill={p.old ? "#9CA3AF" : "#111827"}>
                            {p.value}
                        </text>
                        {labelled(i) ? (
                            <text x={p.x} y={H - 8} textAnchor={i === lastI && points.length > 1 ? "end" : i === 0 && points.length > 1 ? "start" : "middle"} fontSize="12" fill="#9CA3AF">
                                {(() => {
                                    const n = scanOf?.(p.date) ?? null;
                                    return n ? (
                                        <>
                                            <tspan fontWeight="600" fill={p.old ? "#9CA3AF" : "#374151"}>V{n}</tspan> · {dayMonth(p.date)}
                                        </>
                                    ) : (
                                        dayMonth(p.date)
                                    );
                                })()}
                            </text>
                        ) : (
                            <line x1={p.x} y1={BOT} x2={p.x} y2={BOT + 4} stroke="#D1D5DB" />
                        )}
                    </g>
                );
            })}
            {/* ONE POINT ON THE CURRENT QUESTION SET, however many grey ones
                sit behind it (#4025 FE-6).
    
                The test was `points.length === 1`, which counts the old points
                too -- so after a question change the chart drew a single new
                dot, no line (the line needs `current.length > 1`) and no
                caption either, because there were older dots in the array. A
                lone dot with nothing saying why is the one state this caption
                exists for.
    
                Behind a break the caption is NOT drawn here: the rule's own label
                sits on the same line and the two ran into each other. The note
                under the chart says it instead (NamedOverTime below). */}
            {current.length === 1 && old.length === 0 && (
                <text x={X1} y={TOP - 4} textAnchor="end" fontSize="12" fill="#9CA3AF">
                    First read. The line starts next scan.
                </text>
            )}
        </svg>
    );
}

/* ------------------------------------------------------------------------- *
 * Staff: the line by question set
 * ------------------------------------------------------------------------- */

interface VersionedPoint {
    date: string | null;
    value: number;
    /** Null for a scan whose question set is not on record: drawn grey, never joined. */
    version: number | null;
    /**
     * The line breaks here: ChatGPT was asked another way from this scan on, or
     * the backend stamped a break at it inside the same set (#4192).
     */
    split: boolean;
    /** The break is the change of how ChatGPT was asked, the one the caption names. */
    methodSplit: boolean;
    /** The backend's own break, inside one set: its sentence is `words.why`, not a method change. */
    stamped: boolean;
}

/** A run of consecutive scans on one set. A set restored later starts a run of its own. */
interface Run {
    n: number | null;
    from: number;
    to: number;
}
type SetRun = Run & { n: number };
const isSetRun = (r: Run): r is SetRun => r.n !== null;
const setLabel = (n: number | null) => (n === null ? "a question set not on record" : versionName(n));

/**
 * Runs of one set that touch (split only by a change of how ChatGPT was
 * asked) as one stretch: what a band and a tab sit over. A set restored after
 * another stays two stretches.
 */
function stretchesOf(runs: Run[]): SetRun[] {
    const out: SetRun[] = [];
    runs.filter(isSetRun).forEach((r) => {
        const prev = out[out.length - 1];
        if (prev && prev.n === r.n && prev.to === r.from - 1) prev.to = r.to;
        else out.push({ ...r });
    });
    return out;
}

const TAB_ROOM = 22;
/** The least room a date label needs: "13 Oct" at 12px, with air either side. */
const LABEL_GAP = 46;
/** A second label line, the time, when two scans share a day. */
const TIME_ROOM = 14;

function runsOf(points: VersionedPoint[]): Run[] {
    const runs: Run[] = [];
    points.forEach((p, i) => {
        const prev = runs[runs.length - 1];
        // A run is one set asked the same way: a new set, a scan with no set
        // on record, or a change of how ChatGPT was asked each start another.
        if (prev && p.version !== null && prev.n === p.version && prev.to === i - 1 && !p.split) prev.to = i;
        else runs.push({ n: p.version, from: i, to: i });
    });
    return runs;
}

function VersionedSvg({
    points,
    selected,
    currentVersion,
    onSelect,
    onOpen,
    W,
    H2,
    crowded,
}: {
    points: VersionedPoint[];
    selected: number;
    currentVersion: number | null;
    onSelect: (n: number) => void;
    /** Opens Question history at a set: the picked set's tab, clicked again. */
    onOpen: (n: number) => void;
    W: number;
    H2: number;
    crowded: Set<string>;
}) {
    const top0 = TOP + TAB_ROOM;
    const bot = H2 - 28 - (crowded.size ? TIME_ROOM : 0);
    const step = gridStep(Math.max(...points.map((p) => p.value)));
    const top = step * 3;
    // The band reaches PAD either side of its outer points, so the first and
    // last points sit far enough in for it to stay whole and centred.
    const PAD = 22;
    const p0 = X0 + PAD + 8;
    const p1 = W - PAD - 8;
    // Scans sit at most MAX_GAP apart and the series is centred, so a few
    // scans on a wide card read as one line rather than scattered dots.
    const MAX_GAP = 88;
    const gap = points.length > 1 ? Math.min(MAX_GAP, (p1 - p0) / (points.length - 1)) : 0;
    const start = p0 + (p1 - p0 - gap * (points.length - 1)) / 2;
    // Never below 2, so a dense history still draws a band of real width.
    const pad = points.length > 1 ? Math.max(2, Math.min(PAD, gap / 2 - 4)) : PAD;
    const x = (i: number) => start + i * gap;
    const y = (v: number) => bot - (v / top) * (bot - top0);
    const runs = runsOf(points);
    const stretches = stretchesOf(runs);
    const xy = points.map((p, i) => ({ ...p, x: x(i), y: y(p.value) }));
    const label = points.map((p) => `${dayMonth(p.date) || "undated"}: ${p.value} on ${setLabel(p.version)}`).join("; ");
    // Which points carry a date label: on a dense chart they would run into
    // each other ("9 Sep10 Sep14 Sep"), so labels keep LABEL_GAP apart, the
    // newest scan first, then the picked set's ends, then where each set
    // starts, then the rest from newest to oldest. Every point keeps its
    // date in its tooltip.
    const labelled = (() => {
        const order: number[] = [points.length - 1];
        runs.filter((r) => r.n === selected).forEach((r) => order.push(r.to, r.from));
        runs.forEach((r) => order.push(r.from));
        for (let i = points.length - 1; i >= 0; i -= 1) order.push(i);
        const kept: number[] = [];
        order.forEach((i) => {
            if (!kept.includes(i) && kept.every((k) => Math.abs(x(k) - x(i)) >= LABEL_GAP)) kept.push(i);
        });
        return new Set(kept);
    })();

    return (
        <svg width={W} height={H2} viewBox={`0 0 ${W} ${H2}`} className="block" role="group" aria-label={`Questions named per scan, by question set. ${label}`}>
            {/* The picked set's band, behind everything: centred on its
                points and hanging from its tab. */}
            {stretches.filter((r) => r.n === selected).map((r) => (
                <rect
                    key={`band-${r.from}`}
                    x={x(r.from) - pad}
                    y={12}
                    width={x(r.to) - x(r.from) + pad * 2}
                    height={bot - 12}
                    rx={8}
                    fill="#0B49FE"
                    fillOpacity="0.05"
                />
            ))}
            {[1, 2, 3].map((k) => (
                <g key={k}>
                    <line x1={X0} y1={y(step * k)} x2={W - 8} y2={y(step * k)} stroke="#F3F4F6" />
                    <text x={X0 - 8} y={y(step * k) + 4} textAnchor="end" fontSize="12" fill="#9CA3AF">{step * k}</text>
                </g>
            ))}
            <line x1={X0} y1={bot} x2={W - 8} y2={bot} stroke="#E5E7EB" />
            {/* Where one set ends and the next begins. */}
            {runs.slice(1).map((r) => {
                const bx = (x(r.from - 1) + x(r.from)) / 2;
                return <line key={`rule-${r.from}`} x1={bx} y1={TAB_ROOM + 2} x2={bx} y2={bot} stroke="#E5E7EB" strokeDasharray="3 4" />;
            })}
            {/* Neighbouring scans on different sets: a dotted join, not like for like. */}
            {runs.slice(1).filter((r) => {
                const prev = points[r.from - 1].version;
                return prev !== null && r.n !== null && prev !== r.n;
            }).map((r) => {
                const a = xy[r.from - 1];
                const b = xy[r.from];
                return <line key={`dot-${r.from}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} {...DOTTED} />;
            })}
            {/* A line per run: only scans on the same questions join. */}
            {runs.filter((r) => r.to > r.from).map((r) => {
                const on = r.n === selected;
                const pts = xy.slice(r.from, r.to + 1);
                const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                return (
                    <g key={`line-${r.from}`}>
                        {on && <polygon points={`${pts[0].x},${bot} ${line} ${pts[pts.length - 1].x},${bot}`} fill="#0B49FE" fillOpacity="0.06" />}
                        <polyline points={line} fill="none" stroke={on ? "#0B49FE" : "#D1D5DB"} strokeWidth={on ? 2 : 1.5} strokeLinejoin="round" />
                    </g>
                );
            })}
            {xy.map((p, i) => {
                const on = p.version === selected;
                const lastOfRun = runs.some((r) => r.to === i && r.n === selected);
                const crowdedDay = onCrowdedDay(p.date, crowded);
                const pointLabel = `${dayMonth(p.date) || "Undated"}${crowdedDay ? `, ${hourMinute(p.date)}` : ""}: ${p.value} named, ${setLabel(p.version)}`;
                const version = p.version;
                const pick = () => { if (version !== null) onSelect(version); };
                // A point with no set on record has nothing to pick.
                const control = version === null ? {} : {
                    role: "button",
                    tabIndex: 0,
                    "aria-pressed": on,
                    className: "cursor-pointer outline-none [&:focus-visible>circle:nth-of-type(2)]:stroke-blue-700",
                    onClick: pick,
                    onKeyDown: (e: KeyboardEvent<SVGGElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } },
                };
                return (
                    <g key={`${p.date ?? "p"}-${i}`} aria-label={pointLabel} {...control}>
                        <title>{pointLabel}</title>
                        <circle cx={p.x} cy={p.y} r={12} fill="transparent" />
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r={on && lastOfRun ? 4.5 : 3.5}
                            fill={on && lastOfRun ? "#0B49FE" : "#fff"}
                            stroke={on ? "#0B49FE" : "#D1D5DB"}
                            strokeWidth="2"
                        />
                        <text x={p.x} y={p.y - 11} textAnchor="middle" fontSize="13" fontWeight="600" fill={on ? "#111827" : "#9CA3AF"}>
                            {p.value}
                        </text>
                        {labelled.has(i) && (
                            <text x={p.x} y={bot + 20} textAnchor="middle" fontSize="12" fill={on ? "#4B5563" : "#9CA3AF"}>{dayMonth(p.date)}</text>
                        )}
                        {labelled.has(i) && crowdedDay && (
                            <text x={p.x} y={bot + 20 + TIME_ROOM} textAnchor="middle" fontSize="12" fill="#9CA3AF">{hourMinute(p.date)}</text>
                        )}
                    </g>
                );
            })}
            {/* One tab per set, over its latest run (a set restored later
                runs twice but is still one set): picks the set, and on the
                set already picked opens what changed in it. */}
            {stretches.filter((r) => !stretches.some((later) => later.n === r.n && later.from > r.from)).map((r) => {
                const on = r.n === selected;
                const current = r.n === currentVersion;
                const w = current ? 40 : 30;
                const cx = (x(r.from) + x(r.to)) / 2;
                const left = Math.min(Math.max(cx - w / 2, 2), W - w - 2);
                const pick = () => (on ? onOpen(r.n) : onSelect(r.n));
                return (
                    <g
                        key={`tab-${r.from}`}
                        role="button"
                        tabIndex={0}
                        aria-pressed={on}
                        aria-label={on
                            ? `See what changed in question set ${versionName(r.n)}`
                            : `Show question set ${versionName(r.n)}${current ? ", the current set" : ""}`}
                        className="cursor-pointer outline-none [&:focus-visible>rect]:stroke-blue-500"
                        onClick={pick}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
                    >
                        <title>{on ? `${versionName(r.n)}: click to see what changed` : `Show ${versionName(r.n)}`}</title>
                        <rect x={left} y={2} width={w} height={20} rx={6} fill={on ? "#0B49FE" : "#F3F4F6"} stroke="transparent" strokeWidth="2" />
                        {current && <circle cx={left + 9} cy={12} r={2.5} fill={on ? "#fff" : "#0B49FE"} />}
                        <text x={current ? left + 25 : left + w / 2} y={16} textAnchor="middle" fontSize="12" fontWeight="600" fill={on ? "#fff" : "#4B5563"}>
                            {versionName(r.n)}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

function VersionedChart(props: {
    points: VersionedPoint[];
    selected: number;
    currentVersion: number | null;
    onSelect: (n: number) => void;
    onOpen: (n: number) => void;
}) {
    const [ref, width] = useWidth<HTMLDivElement>();
    const crowded = crowdedDays(props.points.map((p) => p.date));
    const H2 = H + TAB_ROOM - 8 + (crowded.size ? TIME_ROOM : 0);
    return (
        <div ref={ref} className="w-full" style={{ height: H2 }}>
            {width > 0 && <VersionedSvg {...props} W={width} H2={H2} crowded={crowded} />}
        </div>
    );
}

/** The picked set in one line, and the way into its diff. */
function VersionCaption({
    points,
    selected,
    firstVersion,
    currentVersion,
    onOpen,
}: {
    points: VersionedPoint[];
    selected: number;
    /** The oldest set on record, shown or not: it has no earlier set to compare with. */
    firstVersion: number;
    currentVersion: number | null;
    onOpen: (n: number) => void;
}) {
    const mine = points.filter((p) => p.version === selected);
    if (mine.length === 0) return null;
    const crowded = crowdedDays(points.map((p) => p.date));
    const when = (d: string | null) => (onCrowdedDay(d, crowded) ? `${dayMonth(d)}, ${hourMinute(d)}` : dayMonth(d));
    const firstP = mine[0];
    const lastP = mine[mine.length - 1];
    const range = mine.length === 1 ? when(firstP.date) : `${when(firstP.date)} to ${when(lastP.date)}`;
    // The change is claimed only over the set's latest run: the same questions,
    // asked the same way. A change of how ChatGPT was asked starts a new run.
    const latest = runsOf(points).filter((r) => r.n === selected).slice(-1)[0];
    const moved = latest && latest.to > latest.from ? points[latest.to].value - points[latest.from].value : null;
    const movedSince = latest && moved !== null && latest.from > points.indexOf(firstP) ? ` since ${when(points[latest.from].date)}` : "";
    const isFirstSet = firstVersion === selected;
    return (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className={cn(TYPE.tc, "font-semibold text-gray-900")}>{versionName(selected)}</span>
            {selected === currentVersion && <span className={cn(TYPE.tc, "font-medium text-blue-600")}>Current</span>}
            <span className={cn(TYPE.tc, "tabular-nums text-gray-500")}>
                {range} · {mine.length} {mine.length === 1 ? "scan" : "scans"}
                {moved !== null && (
                    <> · <span className={moved > 0 ? "text-emerald-700" : moved < 0 ? "text-rose-600" : undefined}>{moved > 0 ? "+" : moved < 0 ? "−" : "±"}{Math.abs(moved)}</span>{movedSince} on the same questions</>
                )}
            </span>
            <button
                type="button"
                onClick={() => onOpen(selected)}
                className={cn(TYPE.tc, "ml-auto inline-flex items-center gap-0.5 font-medium text-blue-600 hover:underline")}
            >
                {isFirstSet ? "See its questions" : "See what changed"}
                <ChevronRightIcon aria-hidden size={13} />
            </button>
        </div>
    );
}

const MENU = "min-w-[220px] rounded-xl border-0 bg-white p-1 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06]";
const MENU_ITEM = cn(TYPE.tc, "cursor-pointer rounded-lg py-2 pl-8 pr-2.5 text-gray-900 focus:bg-gray-100");

/**
 * Which sets the chart takes in: all of them until one is unticked. The menu
 * stays open while sets are ticked, and the last set shown cannot be left out.
 */
function VersionFilter({
    points,
    sets,
    hidden,
    currentVersion,
    onChange,
}: {
    /** Every set's points, shown or not: each row's dates. */
    points: { date: string | null; version: number | null }[];
    sets: number[];
    hidden: number[];
    currentVersion: number | null;
    onChange: (hidden: number[]) => void;
}) {
    const shown = sets.filter((n) => !hidden.includes(n));
    const label = hidden.length === 0
        ? "All versions"
        : shown.length <= 3 ? shown.map(versionName).join(", ") : `${shown.length} of ${sets.length} versions`;
    const span = (n: number) => {
        const mine = points.filter((p) => p.version === n);
        const first = dayMonth(mine[0]?.date ?? null);
        const last = dayMonth(mine[mine.length - 1]?.date ?? null);
        return first === last ? first : `${first} to ${last}`;
    };
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <PillButton size="sm" icon={ListFilterIcon} className={hidden.length ? "text-blue-600" : undefined} aria-label={`Versions on the chart: ${label}`}>
                    {label}
                </PillButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={MENU}>
                {sets.map((n) => {
                    const on = !hidden.includes(n);
                    return (
                        <DropdownMenuCheckboxItem
                            key={n}
                            checked={on}
                            disabled={on && shown.length === 1}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={() => onChange(on ? [...hidden, n] : hidden.filter((h) => h !== n))}
                            className={MENU_ITEM}
                        >
                            <span className="font-semibold">{versionName(n)}</span>
                            {n === currentVersion && <span className="ml-1.5 font-medium text-blue-600">Current</span>}
                            <span className="ml-auto pl-4 tabular-nums text-gray-500">{span(n)}</span>
                        </DropdownMenuCheckboxItem>
                    );
                })}
                {hidden.length > 0 && (
                    <>
                        <DropdownMenuSeparator className="mx-1 my-1 bg-gray-100" />
                        <DropdownMenuItem className={cn(MENU_ITEM, "text-blue-600")} onSelect={() => onChange([])}>
                            Show all versions
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

/** The words for a break, by what changed. The merchant is not told how we ask. */
function breakWords(cause: VisTrendBreakCause | null, staff: boolean) {
    if (cause === "questions") return { mark: "Questions changed", old: "on different questions", why: "used different questions" };
    if (cause === "instrument" && staff) {
        return { mark: "ChatGPT asked differently", old: "ChatGPT asked another way", why: "asked ChatGPT another way" };
    }
    return { mark: "Measured differently", old: "measured differently", why: "was measured differently" };
}

/** The operator's detail, folded under the chart. Staff only. */
function HowMeasured({ rows }: { rows: VisOpsRow[] }) {
    return (
        <details className="group mt-4">
            <summary className={cn(TYPE.tc, "inline-flex cursor-pointer list-none items-center gap-1.5 font-medium text-blue-600 [&::-webkit-details-marker]:hidden")}>
                <ShieldCheckIcon aria-hidden size={13} />
                How this was measured
                <ChevronRightIcon aria-hidden size={12} className="transition-transform group-open:rotate-90" />
            </summary>
            <dl className={cn(TYPE.tc, "m-0 mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-[9rem_minmax(0,1fr)]")}>
                {rows.map((r) => (
                    <div key={r.label} className="contents">
                        <dt className="inline-flex items-center gap-1.5 text-gray-500">
                            {r.engine && <EngineLogo engine={r.engine} size={13} />}
                            {r.label}
                        </dt>
                        <dd className={cn("m-0 min-w-0 break-words", r.tone === "warn" ? "text-amber-800" : "text-gray-900")}>{r.value}</dd>
                    </div>
                ))}
            </dl>
        </details>
    );
}

export function NamedOverTime({
    model,
    engine,
    staff,
    right,
    versionOf,
    methodOf,
    currentVersion,
    shownVersion,
    onPickVersion,
    onOpenVersion,
    scanOf,
    notedOn = [],
}: {
    /**
     * The merchant's version for a point, by its date: numbered by question set,
     * as the scan picker and the staff page number it (#4657). Given, the chart
     * labels each point by it (V1, V2) on one plain line.
     */
    scanOf?: (date: string | null) => number | null;
    /** Dates on the line measured differently with the same questions (`measureNotes`): a note, never a break (#4657). */
    notedOn?: string[];
    model: VisModel;
    engine: EngineFilter;
    staff: boolean;
    /** "Checked 16 Sep 2026", or the merchant's next check. */
    right: string;
    /** The question set a point (by its date) was asked on, from the score history. */
    versionOf?: (date: string | null) => number | null;
    /** How ChatGPT was asked on that scan ("chatgpt_app", "openai"), when the history says. */
    methodOf?: (date: string | null) => string | null;
    /** The set the newest scan was asked on ("Current"). */
    currentVersion?: number | null;
    /** The set the scan on screen was asked on, when the page can switch it. */
    shownVersion?: number | null;
    /**
     * Puts a set's latest scan on screen, so the whole page shows its
     * questions. Given, a pick is the page's, not the chart's own, and the
     * picked set is always the one on screen.
     */
    onPickVersion?: (n: number) => void;
    /** Opens Question history at a set. */
    onOpenVersion?: (n: number) => void;
}) {
    const { trend } = model;
    const who = staff ? "the business" : "you";
    const engineName = engine === "all" ? null : model.engines[engine].label;
    const points: ChartPoint[] = trend.points
        .map((p, i) => ({ date: p.date, value: p.values[engine], of: p.of[engine], old: trend.breakIndex !== null && i < trend.breakIndex, set: versionOf?.(p.date) ?? null }))
        .filter((p): p is ChartPoint => p.value !== null);
    const words = breakWords(trend.breakCause, staff);
    // Whether the chart draws a dotted join anywhere: a change of set between neighbours.
    const anyDotted = points.slice(1).some((p, i) => joinKind(points[i], p, trend.breakCause) === "dotted");
    const currentCount = points.filter((p) => !p.old).length;
    const versionedDotted = (pts: VersionedPoint[]) => pts.slice(1).some((p, i) => pts[i].version !== null && p.version !== null && pts[i].version !== p.version);
    const lastOld = [...points].reverse().find((p) => p.old);
    const firstNew = points.find((p) => !p.old);
    const unitWord = trend.unit === "questions" ? "Questions named" : "Answers that name " + who;
    // The mentions series cannot be split by engine. With one engine chosen,
    // the card states that engine's current figure and says the line is not
    // kept yet, rather than drawing the all-engine line under its name.
    const byEngineUnrecorded = trend.unit === "answers" && engine !== "all";
    const stat = engine === "all" ? null : model.engines[engine];

    // When the history names a set for the points: the line by set, from the
    // first set on (#4192): a business with one set still shows which version
    // its scans were asked on (v1), the caption and its questions.
    // Any scan without a set on record stays on the chart as a grey point
    // rather than switching the versions off, and a change of how ChatGPT was
    // asked (the API against the app), or a break the backend stamped inside
    // the set, breaks the line where it happened. Gemini and Google's AI alone
    // are not affected by the method.
    const everySet = (() => {
        if (scanOf || !versionOf || !onOpenVersion || trend.unit !== "questions") return null;
        const out = points.map((p, i) => {
            const version = versionOf(p.date);
            const before = i > 0 ? points[i - 1] : null;
            // The first scan after a break the backend stamped, on the set the
            // scan before it was asked on: a change of set already starts a run.
            const stamped = !!before && before.old && !p.old && version !== null && versionOf(before.date) === version;
            return { date: p.date, value: p.value, version, stamped };
        });
        return new Set(out.flatMap((p) => (p.version === null ? [] : [p.version]))).size >= 1 ? out : null;
    })();
    const sets = everySet ? Array.from(new Set(everySet.flatMap((p) => (p.version === null ? [] : [p.version])))).sort((a, b) => a - b) : [];
    // The sets left out of the chart (the Versions menu). Kept per business,
    // by its scan dates: an engine switch keeps it, another business starts
    // with every set, and coming back to one restores its own. A scan with no
    // set on record shows only with all.
    const datesKey = trend.points.map((p) => p.date ?? "").join("|");
    const [filters, setFilters] = useState<Record<string, number[]>>({});
    const leftOut = (filters[datesKey] ?? []).filter((n) => sets.includes(n));
    const hidden = leftOut.length < sets.length ? leftOut : [];
    const methodMatters = engine === "all" || engine === "chatgpt";
    const versionedPoints: VersionedPoint[] | null = everySet
        ? everySet
            .filter((p) => hidden.length === 0 || (p.version !== null && !hidden.includes(p.version)))
            .map((p, i, shown) => {
                // A change of how ChatGPT was asked between two scans shown side by side.
                const before = i > 0 && methodOf ? methodOf(shown[i - 1].date) : null;
                const now = methodOf ? methodOf(p.date) : null;
                const methodSplit = methodMatters && !!before && !!now && before !== now;
                return { ...p, split: methodSplit || p.stamped, methodSplit };
            })
        : null;
    const splitDates = (versionedPoints ?? []).filter((p) => p.methodSplit).map((p) => dayMonth(p.date)).filter(Boolean);
    // A break stamped inside one set that is not a change of method: said once, in the plain chart's words.
    const stampedBreak = (versionedPoints ?? []).some((p) => p.stamped && !p.methodSplit) && lastOld && firstNew ? { from: lastOld.date, to: firstNew.date } : null;
    const splitWhen = splitDates.length > 1 ? `${splitDates.slice(0, -1).join(", ")} and ${splitDates[splitDates.length - 1]}` : splitDates[0] ?? "";
    const fallbackVersion = versionedPoints ? [...versionedPoints].reverse().find((p) => p.version !== null)?.version ?? null : null;
    // A pick belongs to the series it was made on: another scan or business
    // on screen starts on its own set again, even with the same set number.
    // The Versions menu does not reset it: a pick still shown stays picked.
    const seriesKey = everySet ? everySet.map((p) => `${p.date ?? "undated"}:${p.version}:${p.value}`).join("|") : "plain";
    const [selection, setSelection] = useState<{ key: string; version: number | null }>({ key: seriesKey, version: null });
    const picked = selection.key === seriesKey && versionedPoints?.some((p) => p.version === selection.version) ? selection.version : null;
    const setPicked = (version: number) => setSelection({ key: seriesKey, version });
    // The current set leads only when it has a point on this chart: an engine
    // filter, or a set with no plotted scan yet, can leave it out, and then
    // the latest plotted set leads instead of nothing being picked.
    const plottedCurrent = currentVersion != null && versionedPoints?.some((p) => p.version === currentVersion) ? currentVersion : null;
    const plottedShown = shownVersion != null && versionedPoints?.some((p) => p.version === shownVersion) ? shownVersion : null;
    const selectedVersion = onPickVersion ? plottedShown ?? plottedCurrent ?? fallbackVersion : picked ?? plottedCurrent ?? fallbackVersion;

    return (
        <FoldCard
            alwaysOpen
            id="trend"
            title={trend.unit === "questions" ? `Named over time${engineName ? ` on ${engineName}` : ""}` : `Answers that name ${who}, per scan`}
            icon={{ icon: TrendingUpIcon, area: "ai" }}
            right={right}
        >
            {((engine === "all" && trend.unit === "questions") || (everySet && onOpenVersion && points.length > 0)) && (
                <div className="mb-3 flex items-start gap-3">
                    {engine === "all" && trend.unit === "questions" && (
                        // The same sentence as the headline tile and the type grid:
                        // one rule, said wherever the number is (#4025 FE-5).
                        <p className={cn(TYPE.tm, "m-0 min-w-0 flex-1 text-gray-500")}>{namedRule(model.counted, staff)}</p>
                    )}
                    {everySet && onOpenVersion && points.length > 0 && sets.length > 1 && (
                        // Choosing between sets needs two of them (#4192).
                        <span className="ml-auto shrink-0">
                            <VersionFilter
                                points={everySet}
                                sets={sets}
                                hidden={hidden}
                                currentVersion={currentVersion ?? null}
                                onChange={(next) => setFilters((prev) => ({ ...prev, [datesKey]: next }))}
                            />
                        </span>
                    )}
                </div>
            )}
            {byEngineUnrecorded && stat ? (
                <div>
                    <p className={cn(TYPE.num, "m-0 text-gray-900")}>
                        {stat.named !== null && stat.of !== null ? <OfValue n={stat.named} of={stat.of} /> : "–"}
                    </p>
                    <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                        {staff
                            ? `${stat.label} on this scan. A line per engine starts once the history records each engine.`
                            : `${stat.label} at your latest check. The line for one engine is not kept yet.`}
                    </p>
                </div>
            ) : points.length === 0 ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Nothing to chart on this scan.</p>
            ) : versionedPoints && selectedVersion !== null && onOpenVersion ? (
                <>
                    <VersionedChart
                        points={versionedPoints}
                        selected={selectedVersion}
                        currentVersion={currentVersion ?? null}
                        onSelect={onPickVersion ?? setPicked}
                        onOpen={onOpenVersion}
                    />
                    <VersionCaption
                        points={versionedPoints}
                        selected={selectedVersion}
                        firstVersion={sets[0]}
                        currentVersion={currentVersion ?? null}
                        onOpen={onOpenVersion}
                    />
                    {versionedDotted(versionedPoints) && (
                        <>
                            <LineKey />
                            <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>{DOTTED_NOTE}</p>
                        </>
                    )}
                    {splitWhen && (
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                            {staff
                                ? `ChatGPT was asked another way from ${splitWhen}, so the line does not join across ${splitDates.length > 1 ? "those dates" : "that date"}.`
                                : `One assistant was measured differently from ${splitWhen}, so the line does not join across ${splitDates.length > 1 ? "those dates" : "that date"}.`}
                        </p>
                    )}
                    {stampedBreak && (
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")} title={staff ? trend.breakReason ?? undefined : undefined}>
                            {dayMonth(stampedBreak.from)} {words.why}, so the line does not join to {dayMonth(stampedBreak.to)}.
                        </p>
                    )}
                </>
            ) : (
                <>
                    <TrendChart points={points} unit={unitWord} breakLabel={words.mark} oldLabel={words.old} scanOf={scanOf} breakCause={trend.breakCause} />
                    {anyDotted ? (
                        <>
                            <LineKey />
                            <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")} title={staff ? trend.breakReason ?? undefined : undefined}>
                                {DOTTED_NOTE}
                            </p>
                        </>
                    ) : lastOld && firstNew && (
                        // Measured another way (not new questions): the line still starts after the rule.
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")} title={staff ? trend.breakReason ?? undefined : undefined}>
                            {dayMonth(lastOld.date)} {words.why}, so the line starts {dayMonth(firstNew.date)}.
                            {currentCount === 1 && " It is one scan so far; the line joins up from the next one."}
                        </p>
                    )}
                    {notedOn.length > 0 && (
                        // Same questions, measured a little differently: the line still joins (#4657).
                        <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                            {notedOn.map((d) => dayMonth(d)).filter(Boolean).join(", ")} {notedOn.length > 1 ? "were" : "was"} measured a little differently, on the same questions, so the line still joins.
                        </p>
                    )}
                    {trend.unit === "answers" && (
                        <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>
                            Each point counts the answers that named {who}, all engines together.
                            {staff && " Questions named per scan are not recorded yet."}
                        </p>
                    )}
                </>
            )}
            {staff && <HowMeasured rows={model.ops} />}
        </FoldCard>
    );
}
